import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  documentToHtmlString,
  Options,
} from '@contentful/rich-text-html-renderer';
import {
  Document as ContentfulDocument,
  BLOCKS,
} from '@contentful/rich-text-types';
import { ContentfulService } from '../contentful-api/contentful.service';
import { Asset } from 'contentful';

@Injectable({
  providedIn: 'root',
})
export class RichTextHtmlRendererService {
  constructor(
    private sanitizer: DomSanitizer,
    private contentfulService: ContentfulService,
  ) {}

  async renderRichText(
    contentfulDoc: ContentfulDocument | null | undefined,
  ): Promise<SafeHtml> {
    if (!contentfulDoc || contentfulDoc.nodeType !== 'document') {
      return this.sanitizer.bypassSecurityTrustHtml('<p>Invalid content</p>');
    }

    try {
      const htmlString = await this.convertRichTextToHtml(contentfulDoc);
      return this.sanitizer.bypassSecurityTrustHtml(htmlString);
    } catch (error) {
      console.error('Error rendering rich text:', error);
      return this.sanitizer.bypassSecurityTrustHtml(
        '<p>Error rendering content</p>',
      );
    }
  }

  private async convertRichTextToHtml(
    contentfulDoc: ContentfulDocument,
  ): Promise<string> {
    const assetMap = await this.fetchAllAssets(contentfulDoc);

    const customOptions: Options = {
      renderNode: {
        [BLOCKS.EMBEDDED_ASSET]: (node) => {
          const assetId = node?.data?.['target']?.sys?.id;
          const asset = assetMap.get(assetId);

          if (asset) {
            const url = asset.fields.file.url.startsWith('//')
              ? `https:${asset.fields.file.url}`
              : asset.fields.file.url;
            const altText = asset.fields.title || 'Embedded Asset';
            return `<img src="${url}" alt="${altText}" style="max-width: 100%; height: auto;">`;
          }
          return '<!-- Embedded asset not found -->';
        },
      },
    };

    return documentToHtmlString(contentfulDoc, customOptions);
  }

  private async fetchAllAssets(
    contentfulDoc: ContentfulDocument,
  ): Promise<Map<string, any>> {
    const assetIds = new Set<string>();

    // Collect asset IDs from the document
    const collectAssetIds = (node: any) => {
      if (
        node.nodeType === BLOCKS.EMBEDDED_ASSET &&
        node.data?.target?.sys?.id
      ) {
        assetIds.add(node.data.target.sys.id);
      }
      if (node.content) {
        node.content.forEach(collectAssetIds);
      }
    };
    collectAssetIds(contentfulDoc);

    // Fetch assets in bulk
    const assetPromises = Array.from(assetIds).map((id) =>
      this.contentfulService.fetchAssetById(id),
    );

    const assets = await Promise.all(assetPromises);
    const assetMap = new Map<string, any>();
    assets.forEach((asset: Asset) => {
      assetMap.set(asset.sys.id, asset);
    });

    return assetMap;
  }
}
